import { useEffect, useState } from "react";
import { Building2, Loader2, ChevronDown, ChevronUp, Car, Save } from "lucide-react";
import { Card } from "@/components/ui/card";
import { getLinkedDealers, updateDealerCredit, getDealerInventoryForFloorPlan } from "@/Calls/callFloorPlanAuth";

interface DealerLink {
    id: number; dealer_id: number; credit_limit: number; available_credit: number;
    status: string; interest_rate: string | null; notes: string | null;
    dealer: { dealer_name: string; dealer_phone?: string; dealer_email?: string } | null;
}

interface Vehicle {
    vid: number; vyear: string | null; vmake: string | null; vmodel: string | null;
    vvin: string | null; vmileage: string | null; vprice: string | null;
}

function DealerRow({ link, onUpdate }: { link: DealerLink; onUpdate: () => void }) {
    const [expanded,  setExpanded]  = useState(false);
    const [vehicles,  setVehicles]  = useState<Vehicle[]>([]);
    const [loadingV,  setLoadingV]  = useState(false);
    const [editing,   setEditing]   = useState(false);
    const [saving,    setSaving]    = useState(false);
    const [form, setForm] = useState({
        credit_limit:     link.credit_limit,
        available_credit: link.available_credit,
        interest_rate:    link.interest_rate ?? "",
        notes:            link.notes ?? "",
    });

    const toggleExpand = async () => {
        if (!expanded && vehicles.length === 0) {
            setLoadingV(true);
            try {
                const d = await getDealerInventoryForFloorPlan(link.dealer_id);
                setVehicles(d.vehicles ?? []);
            } catch { /* ignore */ }
            finally { setLoadingV(false); }
        }
        setExpanded(!expanded);
    };

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        try {
            await updateDealerCredit(link.dealer_id, {
                credit_limit:     Number(form.credit_limit),
                available_credit: Number(form.available_credit),
                interest_rate:    form.interest_rate || undefined,
                notes:            form.notes || undefined,
            });
            setEditing(false);
            onUpdate();
        } catch (err: any) {
            alert(err.response?.data?.message ?? "Update failed");
        } finally {
            setSaving(false);
        }
    };

    const used = link.credit_limit - link.available_credit;
    const pct  = link.credit_limit > 0 ? (used / link.credit_limit) * 100 : 0;
    const fmt  = (n: number) => n >= 1_000_000 ? `$${(n/1_000_000).toFixed(1)}M` : n >= 1_000 ? `$${(n/1_000).toFixed(0)}K` : `$${n}`;

    return (
        <Card className="overflow-hidden">
            <div className="p-4">
                <div className="flex items-start justify-between">
                    <div>
                        <p className="font-semibold">{link.dealer?.dealer_name ?? `Dealer #${link.dealer_id}`}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                            {fmt(link.available_credit)} available of {fmt(link.credit_limit)} total
                            {link.interest_rate && ` · ${link.interest_rate}% APR`}
                        </p>
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={() => setEditing(!editing)}
                            className="px-3 py-1 rounded-lg border text-xs hover:bg-muted transition-colors"
                        >
                            Edit Credit
                        </button>
                        <button
                            onClick={toggleExpand}
                            className="px-3 py-1 rounded-lg border text-xs hover:bg-muted transition-colors flex items-center gap-1"
                        >
                            Inventory
                            {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                        </button>
                    </div>
                </div>

                <div className="mt-3 h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div
                        className={`h-full rounded-full transition-all ${pct > 80 ? "bg-red-500" : pct > 60 ? "bg-orange-500" : "bg-emerald-500"}`}
                        style={{ width: `${pct}%` }}
                    />
                </div>
                <p className="text-xs text-muted-foreground mt-1">{pct.toFixed(0)}% credit utilized</p>
            </div>

            {/* Edit form */}
            {editing && (
                <div className="border-t px-4 py-4 bg-muted/20">
                    <form onSubmit={handleSave} className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-xs font-medium">Credit Limit ($)</label>
                                <input
                                    type="number" step="1000"
                                    value={form.credit_limit}
                                    onChange={(e) => setForm({ ...form, credit_limit: Number(e.target.value) })}
                                    className="w-full mt-1 px-3 py-1.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-background"
                                />
                            </div>
                            <div>
                                <label className="text-xs font-medium">Available Credit ($)</label>
                                <input
                                    type="number" step="1000"
                                    value={form.available_credit}
                                    onChange={(e) => setForm({ ...form, available_credit: Number(e.target.value) })}
                                    className="w-full mt-1 px-3 py-1.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-background"
                                />
                            </div>
                            <div>
                                <label className="text-xs font-medium">Interest Rate (%)</label>
                                <input
                                    type="text"
                                    value={form.interest_rate}
                                    onChange={(e) => setForm({ ...form, interest_rate: e.target.value })}
                                    className="w-full mt-1 px-3 py-1.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-background"
                                    placeholder="e.g. 4.5"
                                />
                            </div>
                            <div>
                                <label className="text-xs font-medium">Notes</label>
                                <input
                                    type="text"
                                    value={form.notes}
                                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                                    className="w-full mt-1 px-3 py-1.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-background"
                                />
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <button type="button" onClick={() => setEditing(false)} className="px-3 py-1.5 border rounded-lg text-sm hover:bg-muted">
                                Cancel
                            </button>
                            <button type="submit" disabled={saving} className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-sm flex items-center gap-1.5 disabled:opacity-60">
                                {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                                Save
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* Inventory */}
            {expanded && (
                <div className="border-t">
                    {loadingV ? (
                        <div className="flex justify-center py-4">
                            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                        </div>
                    ) : vehicles.length === 0 ? (
                        <p className="text-sm text-muted-foreground text-center py-4">No inventory found</p>
                    ) : (
                        <table className="w-full text-sm">
                            <thead className="bg-muted/30 border-b">
                                <tr className="text-left">
                                    <th className="px-4 py-2 font-medium text-xs">Vehicle</th>
                                    <th className="px-4 py-2 font-medium text-xs">VIN</th>
                                    <th className="px-4 py-2 font-medium text-xs">Mileage</th>
                                    <th className="px-4 py-2 font-medium text-xs">Price</th>
                                </tr>
                            </thead>
                            <tbody>
                                {vehicles.map((v) => (
                                    <tr key={v.vid} className="border-b last:border-0 hover:bg-muted/20">
                                        <td className="px-4 py-2 flex items-center gap-2">
                                            <Car className="h-3.5 w-3.5 text-muted-foreground" />
                                            {v.vyear} {v.vmake} {v.vmodel}
                                        </td>
                                        <td className="px-4 py-2 font-mono text-xs text-muted-foreground">{v.vvin ?? "—"}</td>
                                        <td className="px-4 py-2 text-muted-foreground">
                                            {v.vmileage ? `${parseInt(v.vmileage).toLocaleString()} mi` : "—"}
                                        </td>
                                        <td className="px-4 py-2 font-medium">
                                            {v.vprice ? `$${parseFloat(v.vprice).toLocaleString()}` : "—"}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}
        </Card>
    );
}

export default function FloorPlanDealersPage() {
    const [dealers, setDealers] = useState<DealerLink[]>([]);
    const [loading, setLoading] = useState(true);

    const load = () => {
        setLoading(true);
        getLinkedDealers()
            .then((d) => setDealers(d.dealers ?? []))
            .catch(console.error)
            .finally(() => setLoading(false));
    };

    useEffect(() => { load(); }, []);

    if (loading) return (
        <div className="flex justify-center py-24">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
    );

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-semibold">Dealer Credit Lines</h1>
                <p className="text-muted-foreground text-sm mt-0.5">{dealers.length} linked dealers</p>
            </div>

            {dealers.length === 0 ? (
                <Card className="p-8 text-center">
                    <Building2 className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" />
                    <p className="text-muted-foreground">No linked dealers yet. Contact admin to link dealers to your account.</p>
                </Card>
            ) : (
                <div className="space-y-4">
                    {dealers.map((d) => (
                        <DealerRow key={d.id} link={d} onUpdate={load} />
                    ))}
                </div>
            )}
        </div>
    );
}
